import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { billTotals } from "../utils/billTotals";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { fmt, today } from "../utils";
import UnitInput from "../components/UnitInput";

const emptyLine = { item_id: "", batch_no: "", expiry: "", manufacture_date: "", qty: "", free_qty: 0, rate: "", discount_pct: 0 };
const emptyItem = { name: "", hsn: "", supply_type: "Taxable", unit: "Strip", gst_rate: 12, manufacturer: "", mrp: 0, reorder_level: 10, barcode: "" };
const emptySupplier = { name: "", address: "", gstin: "", phone: "", email: "", payment_terms: "", dl_no: "" };
const emptyPurchaseDetails = { batch_no: "", manufacture_date: "", expiry: "", qty: "", free_qty: 0, rate: "", discount_pct: 0 };

export default function PurchaseForm() {
  const [companies, setCompanies] = useState([]);
  const [items, setItems] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [date, setDate] = useState(today());
  const [lines, setLines] = useState([{ ...emptyLine }]);
  const [saving, setSaving] = useState(false);
  const [itemOpen, setItemOpen] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [newItem, setNewItem] = useState(emptyItem);
  const [newItemPurchase, setNewItemPurchase] = useState(emptyPurchaseDetails);
  const [newSupplier, setNewSupplier] = useState(emptySupplier);
  const [targetLine, setTargetLine] = useState(0);
  const submitting = useRef(false);
  const requestKey = useRef(null);
  const nav = useNavigate();
  const { id: editId } = useParams();

  useEffect(() => {
    Promise.all([
      api.get("/companies"),
      api.get("/items"),
      editId ? api.get(`/purchases/${editId}`) : Promise.resolve(null),
    ])
      .then(([companyRes, itemRes, purchaseRes]) => {
        setCompanies(companyRes.data);
        setItems(itemRes.data);
        if (purchaseRes?.data) {
          const p = purchaseRes.data;
          setCompanyId(p.company_id || "");
          setInvoiceNo(p.invoice_no || "");
          setDate(p.date || today());
          setLines((p.items || []).map((line) => ({
            item_id: line.item_id || "",
            batch_no: line.batch_no || "",
            expiry: line.expiry || "",
            manufacture_date: line.manufacture_date || "",
            qty: line.qty ?? "",
            free_qty: line.free_qty || 0,
            rate: line.rate ?? "",
            discount_pct: line.discount_pct || 0,
          })));
        }
      })
      .catch(() => toast.error("Purchase data load nahi ho paaya"));
  }, [editId]);

  const createSupplier = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/companies", newSupplier);
      setCompanies((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setCompanyId(data.id);
      setNewSupplier(emptySupplier);
      setSupplierOpen(false);
      toast.success("Supplier master added");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const createItem = async (e) => {
    e.preventDefault();
    if (Number(newItemPurchase.qty) <= 0) return toast.error("Purchase quantity enter karo");
    if (Number(newItemPurchase.rate) <= 0) return toast.error("Purchase rate enter karo");
    if (newItemPurchase.manufacture_date && newItemPurchase.expiry &&
        newItemPurchase.manufacture_date.slice(0, 7) > newItemPurchase.expiry) {
      return toast.error("Manufacturing date expiry ke baad nahi ho sakti");
    }
    try {
      const payload = {
        ...newItem,
        gst_rate: Number(newItem.gst_rate),
        mrp: Number(newItem.mrp) || 0,
        reorder_level: Number(newItem.reorder_level) || 10,
        company_id: companyId || null,
      };
      const { data } = await api.post("/items", payload);
      setItems((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setLines((prev) => prev.map((line, i) => i === targetLine ? {
        ...line,
        item_id: data.id,
        ...newItemPurchase,
      } : line));
      setNewItem(emptyItem);
      setNewItemPurchase(emptyPurchaseDetails);
      setItemOpen(false);
      toast.success("New item + is Purchase Bill ki batch/qty/rate row ready hai");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const company = companies.find((c) => c.id === companyId);
  const interstate = company?.gstin && company.gstin.slice(0, 2) !== "10";

  const setLine = (i, patch) => {
    const next = [...lines];
    next[i] = { ...next[i], ...patch };
    setLines(next);
  };

  const totals = useMemo(() => billTotals(lines, items, interstate), [lines, items, interstate]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting.current) return;
    const started = lines.filter((l) => l.item_id || l.batch_no?.trim() || l.qty !== "" || l.rate !== "" || l.expiry || l.manufacture_date);
    const valid = started.filter((l) => l.item_id && Number(l.qty) > 0 && Number(l.rate) > 0);
    if (!companyId) return toast.error("Company select karo");
    if (!valid.length) return toast.error("Kam se kam ek item add karo");
    if (valid.length !== started.length) return toast.error("Kisi purchase row me item, quantity ya rate incomplete hai — row complete ya delete karo");
    if (valid.some(l => l.manufacture_date && l.expiry && l.manufacture_date.slice(0, 7) > l.expiry)) return toast.error("Manufacturing date expiry ke baad nahi ho sakti");
    submitting.current = true;
    setSaving(true);
    try {
      const payload = {
        company_id: companyId, invoice_no: invoiceNo.trim(), date,
        items: valid.map((l) => ({ ...l, qty: Number(l.qty), free_qty: Number(l.free_qty) || 0, rate: Number(l.rate), discount_pct: Number(l.discount_pct) || 0 })),
      };
      const fingerprint = JSON.stringify(payload);
      if (requestKey.current?.fingerprint !== fingerprint) requestKey.current = { fingerprint, key: crypto.randomUUID() };
      const bill = (await (editId
        ? api.put(`/purchases/${editId}`, payload)
        : api.post("/purchases", payload, { headers: { "Idempotency-Key": requestKey.current.key } })
      )).data;
      toast.success(editId ? `Purchase ${bill.bill_no} updated — edit history saved` : `Purchase ${bill.bill_no} saved — stock updated`);
      nav(editId ? `/print/purchase/${bill.id}` : "/purchases");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Purchase save nahi ho paaya");
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  return (
    <form data-testid="purchase-form" onSubmit={submit} className="sgp-page max-w-6xl">
      <div className="sgp-page-header">
        <h1 className="sgp-page-title">{editId ? "Edit Purchase Bill" : "New Purchase Bill"}</h1>
        <button type="submit" data-testid="purchase-save-btn" disabled={saving}
          className="sgp-primary-btn px-5 disabled:opacity-50 disabled:pointer-events-none">
          {saving ? "Saving..." : editId ? "Save Changes" : "Save Purchase"}
        </button>
      </div>

      <div className="sgp-card p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="sgp-label">Company (Supplier) *</label>
          <div className="flex gap-2">
            <select data-testid="purchase-company-select" required className="sgp-input" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              <option value="">— Select from master —</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button type="button" data-testid="purchase-new-supplier" onClick={() => setSupplierOpen(true)}
              className="shrink-0 px-3 py-2 text-xs font-semibold rounded-md border border-slate-300 hover:border-teal-600 hover:text-teal-700">
              + New
            </button>
          </div>
        </div>
        <div>
          <label className="sgp-label">Supplier Invoice No.</label>
          <input data-testid="purchase-invoice-no" className="sgp-input font-mono" value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
        </div>
        <div>
          <label className="sgp-label">Date</label>
          <input data-testid="purchase-date" type="date" className="sgp-input font-mono" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        {company && (
          <p className="sm:col-span-3 text-xs text-slate-500">
            GSTIN: <span className="font-mono">{company.gstin || "—"}</span> · Terms: {company.payment_terms || "—"} ·{" "}
            <span className={interstate ? "text-indigo-600 font-semibold" : "text-teal-700 font-semibold"}>{interstate ? "IGST (Interstate)" : "CGST + SGST (Bihar)"}</span>
          </p>
        )}
      </div>

      <div className="sgp-table-shell overflow-x-auto">
        <table className="w-full min-w-[1080px]">
          <thead>
            <tr>
              <th className="sgp-th w-64">Item (from master)</th><th className="sgp-th">Batch No.</th><th className="sgp-th">Manufactured</th><th className="sgp-th">Expiry</th>
              <th className="sgp-th text-right">Qty</th><th className="sgp-th text-right">Free</th>
              <th className="sgp-th text-right">Rate</th><th className="sgp-th text-right">Disc%</th>
              <th className="sgp-th text-right">GST%</th><th className="sgp-th text-right">Amount</th><th className="sgp-th"></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const m = items.find((x) => x.id === l.item_id);
              const base = (Number(l.qty) || 0) * (Number(l.rate) || 0);
              const amt = base - base * (Number(l.discount_pct) || 0) / 100;
              return (
                <tr key={i} data-testid={`purchase-line-${i}`}>
                  <td className="sgp-td">
                    <div className="space-y-1">
                      <select data-testid={`purchase-item-select-${i}`} className="sgp-input" value={l.item_id} onChange={(e) => setLine(i, { item_id: e.target.value })}>
                        <option value="">— Select item —</option>
                        {items.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.manufacturer})</option>)}
                      </select>
                      <button type="button" data-testid={`purchase-new-item-${i}`} onClick={() => {
                          setTargetLine(i);
                          setNewItem(emptyItem);
                          setNewItemPurchase({
                            batch_no: l.batch_no || "", manufacture_date: l.manufacture_date || "", expiry: l.expiry || "",
                            qty: l.qty || "", free_qty: l.free_qty || 0, rate: l.rate || "", discount_pct: l.discount_pct || 0,
                          });
                          setItemOpen(true);
                        }}
                        className="text-[11px] font-semibold text-teal-700 hover:text-teal-900">+ New medicine / equipment</button>
                    </div>
                  </td>
                  <td className="sgp-td"><input data-testid={`purchase-batch-${i}`} className="sgp-input font-mono w-28" value={l.batch_no} onChange={(e) => setLine(i, { batch_no: e.target.value })} /></td>
                  <td className="sgp-td"><input data-testid={`purchase-manufacture-${i}`} type="date" className="sgp-input font-mono w-36" value={l.manufacture_date} onChange={(e) => setLine(i, { manufacture_date: e.target.value })} /></td>
                  <td className="sgp-td"><input data-testid={`purchase-expiry-${i}`} type="month" className="sgp-input font-mono w-32" value={l.expiry} onChange={(e) => setLine(i, { expiry: e.target.value })} /></td>
                  <td className="sgp-td"><input data-testid={`purchase-qty-${i}`} type="number" min="0" className="sgp-input font-mono w-20 text-right" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} /></td>
                  <td className="sgp-td"><input data-testid={`purchase-free-${i}`} type="number" min="0" className="sgp-input font-mono w-16 text-right" value={l.free_qty} onChange={(e) => setLine(i, { free_qty: e.target.value })} /></td>
                  <td className="sgp-td"><input data-testid={`purchase-rate-${i}`} type="number" step="0.01" min="0" className="sgp-input font-mono w-24 text-right" value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} /></td>
                  <td className="sgp-td"><input data-testid={`purchase-disc-${i}`} type="number" step="0.01" min="0" className="sgp-input font-mono w-16 text-right" value={l.discount_pct} onChange={(e) => setLine(i, { discount_pct: e.target.value })} /></td>
                  <td className="sgp-td text-right font-mono text-xs">{m ? `${m.gst_rate}%` : "-"}</td>
                  <td className="sgp-td text-right font-mono text-xs">{l.item_id ? fmt(amt) : "-"}</td>
                  <td className="sgp-td">
                    <button type="button" data-testid={`purchase-line-del-${i}`} onClick={() => setLines(lines.length === 1 ? [{ ...emptyLine }] : lines.filter((_, j) => j !== i))}
                      className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="p-3 border-t border-slate-100">
          <button type="button" data-testid="purchase-add-line" onClick={() => setLines([...lines, { ...emptyLine }])}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-dashed border-slate-300 text-slate-600 hover:border-teal-600 hover:text-teal-700">
            <Plus className="h-3.5 w-3.5" /> Add Item Row
          </button>
        </div>
      </div>

      <div className="sgp-card p-5 ml-auto max-w-sm space-y-1.5 text-sm">
        <div className="flex justify-between"><span className="text-slate-500">Taxable Amount</span><span data-testid="purchase-taxable" className="font-mono">{fmt(totals.taxable)}</span></div>
        {!interstate && <>
          <div className="flex justify-between"><span className="text-slate-500">CGST</span><span data-testid="purchase-cgst" className="font-mono">{fmt(totals.cgst)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">SGST</span><span data-testid="purchase-sgst" className="font-mono">{fmt(totals.sgst)}</span></div>
        </>}
        {interstate && <div className="flex justify-between"><span className="text-slate-500">IGST</span><span data-testid="purchase-igst" className="font-mono">{fmt(totals.igst)}</span></div>}
        <div className="flex justify-between"><span className="text-slate-500">Round Off</span><span className="font-mono">{totals.roundOff.toFixed(2)}</span></div>
        <div className="flex justify-between border-t border-slate-200 pt-2 text-base font-bold">
          <span>Grand Total</span><span data-testid="purchase-grand-total" className="font-mono text-teal-700">{fmt(totals.grand)}</span>
        </div>
      </div>

      <Dialog open={supplierOpen} onOpenChange={setSupplierOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Add Supplier to Master</DialogTitle></DialogHeader>
          <form onSubmit={createSupplier} className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="sgp-label">Supplier Name *</label><input required className="sgp-input" value={newSupplier.name} onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })} /></div>
            <div><label className="sgp-label">GSTIN</label><input className="sgp-input font-mono" value={newSupplier.gstin} onChange={(e) => setNewSupplier({ ...newSupplier, gstin: e.target.value.toUpperCase() })} /></div>
            <div><label className="sgp-label">Drug Licence No.</label><input className="sgp-input" value={newSupplier.dl_no} onChange={(e) => setNewSupplier({ ...newSupplier, dl_no: e.target.value })} /></div>
            <div><label className="sgp-label">Phone</label><input className="sgp-input" value={newSupplier.phone} onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })} /></div>
            <div><label className="sgp-label">Email</label><input type="email" className="sgp-input" value={newSupplier.email} onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })} /></div>
            <div className="col-span-2"><label className="sgp-label">Address</label><input className="sgp-input" value={newSupplier.address} onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })} /></div>
            <div className="col-span-2"><label className="sgp-label">Payment Terms</label><input className="sgp-input" placeholder="e.g. 30 days" value={newSupplier.payment_terms} onChange={(e) => setNewSupplier({ ...newSupplier, payment_terms: e.target.value })} /></div>
            <div className="col-span-2 flex justify-end gap-2"><button type="button" onClick={() => setSupplierOpen(false)} className="px-4 py-2 text-sm border rounded-md">Cancel</button><button type="submit" className="px-4 py-2 text-sm font-semibold bg-teal-700 text-white rounded-md">Add Supplier</button></div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={itemOpen} onOpenChange={setItemOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Add Medicine / Equipment to Item Master</DialogTitle></DialogHeader>
          <form onSubmit={createItem} className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="sgp-label">Item Name *</label><input required className="sgp-input" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} /></div>
            <div><label className="sgp-label">HSN Code</label><input className="sgp-input font-mono" value={newItem.hsn} onChange={(e) => setNewItem({ ...newItem, hsn: e.target.value })} /></div>
            <div><label className="sgp-label">Unit / Type</label><UnitInput value={newItem.unit} onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })} /></div>
            <div><label className="sgp-label">GST %</label><select className="sgp-input" value={newItem.gst_rate} onChange={(e) => setNewItem({ ...newItem, gst_rate: e.target.value })}>{[0, 5, 12, 18, 28].map((g) => <option key={g} value={g}>{g}%</option>)}</select></div>
            <div><label className="sgp-label">Supply Tax Category</label><select data-testid="new-item-supply-type" className="sgp-input" value={newItem.supply_type || "Taxable"} onChange={e => setNewItem({ ...newItem, supply_type: e.target.value, gst_rate: e.target.value === "Taxable" ? newItem.gst_rate : 0 })}>{["Taxable", "Nil", "Exempt", "NonGST"].map(value => <option key={value} value={value}>{value === "NonGST" ? "Non-GST" : value}</option>)}</select></div>
            <div><label className="sgp-label">MRP ₹</label><input type="number" min="0" step="0.01" className="sgp-input" value={newItem.mrp} onChange={(e) => setNewItem({ ...newItem, mrp: e.target.value })} /></div>
            <div><label className="sgp-label">Manufacturer</label><input className="sgp-input" value={newItem.manufacturer} onChange={(e) => setNewItem({ ...newItem, manufacturer: e.target.value })} /></div>
            <div><label className="sgp-label">Barcode</label><input className="sgp-input font-mono" value={newItem.barcode} onChange={(e) => setNewItem({ ...newItem, barcode: e.target.value })} /></div>
            <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-600">Purchase details — isi bill ki row mein add honge</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Item master banne ke baad dialog sirf close nahi hoga; ye batch, qty aur rate bhi current Purchase Bill row mein bhar dega.</p>
            </div>
            <div><label className="sgp-label">Batch No.</label><input className="sgp-input font-mono" value={newItemPurchase.batch_no} onChange={(e) => setNewItemPurchase({ ...newItemPurchase, batch_no: e.target.value })} /></div>
            <div><label className="sgp-label">Manufactured</label><input type="date" className="sgp-input font-mono" value={newItemPurchase.manufacture_date} onChange={(e) => setNewItemPurchase({ ...newItemPurchase, manufacture_date: e.target.value })} /></div>
            <div><label className="sgp-label">Expiry</label><input type="month" className="sgp-input font-mono" value={newItemPurchase.expiry} onChange={(e) => setNewItemPurchase({ ...newItemPurchase, expiry: e.target.value })} /></div>
            <div><label className="sgp-label">Qty *</label><input required type="number" min="0.001" step="any" className="sgp-input font-mono" value={newItemPurchase.qty} onChange={(e) => setNewItemPurchase({ ...newItemPurchase, qty: e.target.value })} /></div>
            <div><label className="sgp-label">Free Qty</label><input type="number" min="0" step="any" className="sgp-input font-mono" value={newItemPurchase.free_qty} onChange={(e) => setNewItemPurchase({ ...newItemPurchase, free_qty: e.target.value })} /></div>
            <div><label className="sgp-label">Purchase Rate ₹ *</label><input required type="number" min="0.01" step="0.01" className="sgp-input font-mono" value={newItemPurchase.rate} onChange={(e) => setNewItemPurchase({ ...newItemPurchase, rate: e.target.value })} /></div>
            <div><label className="sgp-label">Discount %</label><input type="number" min="0" step="0.01" className="sgp-input font-mono" value={newItemPurchase.discount_pct} onChange={(e) => setNewItemPurchase({ ...newItemPurchase, discount_pct: e.target.value })} /></div>
            <p className="col-span-2 text-xs text-slate-500">Final stock tabhi post hoga jab poora Purchase Bill <b>Save Purchase</b> hoga.</p>
            <div className="col-span-2 flex justify-end gap-2"><button type="button" onClick={() => setItemOpen(false)} className="px-4 py-2 text-sm border rounded-md">Cancel</button><button type="submit" className="px-4 py-2 text-sm font-semibold bg-teal-700 text-white rounded-md">Add Item to Purchase</button></div>
          </form>
        </DialogContent>
      </Dialog>
    </form>
  );
}

